import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ConfigProvider, InputNumber, Popconfirm, Table, Tag } from 'antd';
import heIL from 'antd/locale/he_IL';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faPrint, faVault } from '@fortawesome/free-solid-svg-icons';
import { Money } from '../components/Money';
import { Loader } from '../components/Loader';
import { EmptyState } from '../components/EmptyState';
import ReportingPeriodFilter from '../components/ReportingPeriodFilter';
import { useRegisterRefresh } from '../components/PageActions';
import { NewBankDepositDrawer } from '../features/cash/NewBankDepositDrawer';
import { PdfDrawer } from '../features/cash/PdfDrawer';
import {
  useBankDeposits,
  useCancelBankDeposit,
  useSafe,
} from '../hooks/useCashFlow';
import { useLocationContext } from '../auth/LocationContext';
import { useProjectContext } from '../auth/ProjectContext';
import { firstOfMonthIsrael, lastOfMonthIsrael, isoToDisplay } from '../utils/dates';
import { selectEnvelopesForTarget } from '../utils/envelopeSelection';
import type { BankDepositSummary, ClosingSummary } from '@deposits/shared';

type View = 'safe' | 'deposits';

export function SafeTab({ view }: { view: View }) {
  const { selectedLocationId } = useLocationContext();
  const { selectedProjectId } = useProjectContext();
  const [selectedKeys, setSelectedKeys] = useState<number[]>([]);
  const [targetAmount, setTargetAmount] = useState<number | null>(null);
  const [depositOpen, setDepositOpen] = useState(false);
  const [pdfDepositId, setPdfDepositId] = useState<number | null>(null);
  const [period, setPeriod] = useState(() => ({ from: firstOfMonthIsrael(), to: lastOfMonthIsrael() }));

  const { data: safe, isFetching: safeFetching, isError: safeError, refetch: refetchSafe } = useSafe(selectedLocationId, selectedProjectId);

  const depositParams = useMemo(() => {
    if (selectedLocationId == null) return null;
    return {
      location_id: selectedLocationId,
      project_id: selectedProjectId ?? undefined,
      from: period.from,
      to: period.to,
      status: 'all' as const,
    };
  }, [selectedLocationId, selectedProjectId, period]);

  // Envelopes belong to one project's safe — drop the selection on switch.
  useEffect(() => {
    setSelectedKeys([]);
  }, [selectedProjectId]);
  const { data: deposits = [], isFetching: depFetching, isError: depError, refetch: refetchDeposits } =
    useBankDeposits(view === 'deposits' ? depositParams : null);

  useRegisterRefresh(useCallback(() => {
    refetchSafe();
    refetchDeposits();
  }, [refetchSafe, refetchDeposits]));

  const cancelMutation = useCancelBankDeposit();

  const envelopes = safe?.envelopes ?? [];
  const selectedEnvelopes = useMemo(
    () => envelopes.filter((e) => selectedKeys.includes(e.id)),
    [envelopes, selectedKeys],
  );

  const selectByAmount = () => {
    if (targetAmount == null || targetAmount <= 0) return;
    // envelopes are already ordered oldest → newest (closing_number ASC).
    const picked = selectEnvelopesForTarget(envelopes, targetAmount);
    setSelectedKeys(picked.map((e) => e.id));
    const total = picked.reduce((s, e) => s + Number(e.amount_to_safe), 0);
    toast.success(`נבחרו ${picked.length} סגירות בסכום ${total.toLocaleString('he-IL')} ₪`);
  };

  if (selectedLocationId == null) return <Loader fullScreen />;

  const envelopeColumns = [
    { title: 'מספר', dataIndex: 'closing_number', width: 80, render: (v: number) => <span dir="ltr">{v}</span> },
    { title: 'תאריך', dataIndex: 'closing_date', width: 110, render: (v: string) => isoToDisplay(v) },
    { title: 'קופה', dataIndex: 'register_name', ellipsis: true, render: (v: string | null) => v ?? '—' },
    { title: 'סכום בכספת', dataIndex: 'amount_to_safe', width: 130, render: (v: number) => <Money amount={v} /> },
  ];

  const depositColumns = [
    { title: 'מספר', dataIndex: 'deposit_number', width: 80, render: (v: number) => <span dir="ltr">{v}</span> },
    { title: 'תאריך', dataIndex: 'deposit_date', width: 110, render: (v: string) => isoToDisplay(v) },
    { title: 'סגירות', dataIndex: 'closing_count', width: 80, render: (v: number) => <span dir="ltr">{v}</span> },
    { title: 'סה"כ', dataIndex: 'total', width: 120, render: (v: number) => <Money amount={v} /> },
    { title: 'מפקיד', dataIndex: 'depositor_name', ellipsis: true, render: (v: string | null) => v ?? '—' },
    {
      title: 'סטטוס',
      dataIndex: 'cancelled_at',
      width: 90,
      render: (v: string | null) =>
        v ? <Tag color="red">מבוטל</Tag> : <Tag color="green">הופקד</Tag>,
    },
    {
      title: 'פעולות',
      width: 160,
      render: (_: unknown, r: BankDepositSummary) => (
        <div className="flex items-center gap-1">
          <Button size="small" icon={<FontAwesomeIcon icon={faPrint} />} onClick={() => setPdfDepositId(r.id)}>
            הדפסה
          </Button>
          {!r.cancelled_at && (
            <Popconfirm
              title="לבטל את ההפקדה?"
              description="הסגירות יחזרו לכספת."
              okText="בטל הפקדה"
              cancelText="חזרה"
              onConfirm={() =>
                cancelMutation.mutate(
                  { id: r.id },
                  {
                    onSuccess: () => toast.success('ההפקדה בוטלה'),
                    onError: (err) => toast.error(err.message || 'שגיאה בביטול'),
                  },
                )
              }
            >
              <Button size="small" danger icon={<FontAwesomeIcon icon={faBan} />}>
                בטל
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {view === 'deposits' && (
        <div className="flex items-center gap-3 flex-wrap pb-3">
          <div className="flex-1" />
          <ReportingPeriodFilter value={period} onChange={(from, to) => setPeriod({ from, to })} />
        </div>
      )}

      {view === 'safe' ? (
        <>
          <div className="bg-blue-bg border border-border rounded p-4 mb-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FontAwesomeIcon icon={faVault} className="text-primary" style={{ fontSize: 18 }} />
              <div>
                <div className="text-secondary-text text-small">יתרת הכספת</div>
                <div className="text-heading-2 font-semibold">
                  <Money amount={safe?.balance ?? 0} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <InputNumber
                value={targetAmount}
                onChange={(v) => setTargetAmount(v)}
                onPressEnter={selectByAmount}
                min={0}
                placeholder="סכום להפקדה"
                style={{ width: 160 }}
              />
              <Button onClick={selectByAmount} disabled={!targetAmount}>
                בחר סגירות אוטומטית
              </Button>
              <Button
                type="primary"
                disabled={selectedEnvelopes.length === 0}
                onClick={() => setDepositOpen(true)}
              >
                צור הפקדה לבנק ({selectedEnvelopes.length})
              </Button>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden">
            <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
              <Table<ClosingSummary>
                rowKey="id"
                columns={envelopeColumns as any}
                dataSource={envelopes}
                loading={safeFetching ? { spinning: true } : false}
                size="small"
                rowSelection={{
                  selectedRowKeys: selectedKeys,
                  onChange: (keys) => setSelectedKeys(keys.map(Number)),
                }}
                pagination={{ defaultPageSize: 50, showSizeChanger: true, size: 'small' }}
                scroll={{ x: 'max-content', y: 'calc(100vh - 420px)' }}
                locale={{
                  emptyText: safeError ? (
                    <EmptyState title="שגיאה בטעינת הכספת" description="נסה שוב או רענן את העמוד" />
                  ) : (
                    <EmptyState title="הכספת ריקה" description="סגור קופה כדי להוסיף כסף לכספת" />
                  ),
                }}
              />
            </ConfigProvider>
          </div>
        </>
      ) : (
        <div className="flex-1 min-h-0 overflow-hidden">
          <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
            <Table<BankDepositSummary>
              rowKey="id"
              columns={depositColumns as any}
              dataSource={deposits}
              loading={depFetching ? { spinning: true } : false}
              size="small"
              pagination={{ defaultPageSize: 50, showSizeChanger: true, size: 'small' }}
              scroll={{ x: 'max-content', y: 'calc(100vh - 360px)' }}
              locale={{
                emptyText: depError ? (
                  <EmptyState title="שגיאה בטעינת ההפקדות" description="נסה שוב או רענן את העמוד" />
                ) : (
                  <EmptyState title="אין הפקדות לבנק בטווח" description="צור הפקדה מהכספת" />
                ),
              }}
            />
          </ConfigProvider>
        </div>
      )}

      <NewBankDepositDrawer
        open={depositOpen}
        onClose={() => setDepositOpen(false)}
        onCreated={(id) => {
          setDepositOpen(false);
          setSelectedKeys([]);
          setPdfDepositId(id);
        }}
        selected={selectedEnvelopes}
      />

      <PdfDrawer
        open={pdfDepositId !== null}
        title="הפקדה לבנק"
        blobPath={pdfDepositId !== null ? `/bank-deposits/${pdfDepositId}/pdf-blob` : null}
        downloadName={`bank-deposit-${pdfDepositId ?? ''}.pdf`}
        onClose={() => setPdfDepositId(null)}
      />
    </div>
  );
}
